import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Shield } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'ldc_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'ldc_cookie_preferences';

// Default cookie categories
const defaultPreferences = {
  essential: true, // Always required, cannot be disabled
  analytics: false,
  marketing: false,
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid layout shift on page load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (saved) {
        try {
          setPreferences(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to parse cookie preferences');
        }
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    // Dispatch event for other parts of the app to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectNonEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay for settings modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/60 z-[9998]" 
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Main Cookie Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        role="dialog"
        aria-label="Cookie consent"
        aria-modal="true"
      >
        <div className="max-w-4xl mx-auto bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden max-h-[85vh] overflow-y-auto">
          {!showSettings ? (
            /* Initial Banner View */
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 bg-violet-100 rounded-full items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-violet-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-violet-600 sm:hidden" />
                    We value your privacy
                  </h2>
                  <p className="text-stone-600 text-sm mb-4">
                    We use cookies to enhance your browsing experience, provide personalised content, 
                    and analyse our traffic. By clicking "Accept All", you consent to our use of cookies. 
                    You can manage your preferences or reject non-essential cookies.
                  </p>
                  <p className="text-stone-500 text-xs mb-4">
                    For more information, please read our{' '}
                    <a href="/privacy" className="text-violet-600 hover:text-violet-700 underline">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/cookies" className="text-violet-600 hover:text-violet-700 underline">
                      Cookie Policy
                    </a>.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-violet-600 hover:bg-violet-500 text-white font-medium"
                      data-testid="cookie-accept-all"
                    >
                      Accept All
                    </Button>
                    <Button
                      onClick={rejectNonEssential}
                      variant="outline"
                      className="border-stone-300 text-stone-700 hover:bg-stone-50"
                      data-testid="cookie-reject-non-essential"
                    >
                      Reject Non-Essential
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                      data-testid="cookie-manage-preferences"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Preferences
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings View */
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-600" />
                  Cookie Preferences
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-stone-400 hover:text-stone-700 p-1 rounded-full"
                  aria-label="Close settings"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-stone-600 text-sm mb-6">
                Manage your cookie preferences below. Essential cookies are required for the website 
                to function and cannot be disabled.
              </p>

              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <h3 className="font-medium text-stone-900">Essential Cookies</h3>
                    </div>
                    <div className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                      Always Active
                    </div>
                  </div>
                  <p className="text-stone-600 text-sm">
                    These cookies are necessary for the website to function properly. They enable core 
                    functionality such as security, user authentication, and remembering your preferences.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-stone-900">Analytics Cookies</h3>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-violet-600' : 'bg-stone-300'
                      }`}
                      role="switch"
                      aria-checked={preferences.analytics}
                      aria-label="Toggle analytics cookies"
                      data-testid="cookie-toggle-analytics"
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  <p className="text-stone-600 text-sm">
                    These cookies help us understand how visitors interact with our website by collecting 
                    and reporting information anonymously. This helps us improve our website and services.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-stone-900">Marketing Cookies</h3>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-violet-600' : 'bg-stone-300'
                      }`}
                      role="switch"
                      aria-checked={preferences.marketing}
                      aria-label="Toggle marketing cookies"
                      data-testid="cookie-toggle-marketing"
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        preferences.marketing ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  <p className="text-stone-600 text-sm">
                    These cookies may be set through our site by advertising partners to build a profile 
                    of your interests and show you relevant adverts on other sites.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={savePreferences}
                  className="bg-violet-600 hover:bg-violet-500 text-white font-medium"
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-stone-300 text-stone-700 hover:bg-stone-50"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Hook to check cookie consent status
export function useCookieConsent() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const loadConsent = () => {
      const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (saved) {
        try {
          setConsent(JSON.parse(saved));
        } catch (e) {
          setConsent(defaultPreferences);
        }
      } else {
        setConsent(null);
      }
    };

    loadConsent();

    // Listen for consent updates
    const handleUpdate = (e) => setConsent(e.detail);
    window.addEventListener('cookieConsentUpdated', handleUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleUpdate);
  }, []);

  return consent;
}

// Utility to check if a specific cookie category is allowed
export function isCookieAllowed(category) {
  const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!saved) return false;
  try {
    const prefs = JSON.parse(saved);
    return prefs[category] === true;
  } catch (e) {
    return false;
  }
}
